import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PurchaseInvoiceDetails from '../components/purchase/PurchaseInvoiceDetails';
import SupplierInformation from '../components/purchase/SupplierInformation';
import InvoiceItemsTable from '../components/sales-invoice/InvoiceItemsTable';
import PaymentInformation from '../components/purchase/PaymentInformation';
import InvoiceSummary from '../components/purchase/InvoiceSummary';
import usePurchaseInvoice from '../hooks/usePurchaseInvoice';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

function PurchaseInvoice() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  // Use custom hook for all invoice logic
  const {
    invoice,
    setInvoice,
    newItem,
    setNewItem,
    visibleColumns,
    setVisibleColumns,
    suppliers,
    products,
    loading,
    productsLoading,
    calculateSubtotal,
    calculateVat,
    calculateTotal,
    getTotalPaid,
    getRemainingBalance,
    handleItemChange,
    handleNewItemChange,
    handleAddItem,
    handleRemoveItem,
    handleAddPayment,
    handleRemovePayment,
    submitInvoice
  } = usePurchaseInvoice(id);

  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'success'
  });
  const [shouldAutoAddItem, setShouldAutoAddItem] = useState(false);

  // Ref for product input to enable auto-focus
  const productInputRef = useRef(null);

  // Set initial focus on product field (only on mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productInputRef.current && !newItem.product) {
        productInputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []); // Run only on mount, not when product changes

  // Auto-add item when product is selected via Enter key
  useEffect(() => {
    if (shouldAutoAddItem && newItem.product && newItem.product.id) {
      console.log('useEffect: Auto-adding item with product:', newItem.product.name);

      handleAddItem();
      setShouldAutoAddItem(false); // Reset flag

      // Focus on Qty field of the newly added row (the row that was just added, not the new empty row)
      setTimeout(() => {
        console.log('Attempting to focus on qty field of newly added row');
        // Find all quantity fields in existing invoice items (not the new item row)
        const allQtyFields = document.querySelectorAll('[data-item-id][data-field="quantity"]');
        console.log('Total quantity fields found:', allQtyFields.length);

        // Get the last quantity field which belongs to the newly added row
        if (allQtyFields.length > 0) {
          const lastQtyField = allQtyFields[allQtyFields.length - 1];
          console.log('Focusing on qty field of the last added item (not new empty row)');
          lastQtyField.focus();
          // No .select() to prevent text selection
        } else {
          console.log('No quantity fields found to focus');
        }
      }, 150);
    }
  }, [shouldAutoAddItem, newItem.product, handleAddItem]);

  // Simple handlers for invoice updates
  const handleSupplierChange = (event, newValue) => {
    setInvoice(prev => ({ ...prev, supplier: newValue }));
  };

  const handleDateChange = (newDate) => {
    setInvoice(prev => ({ ...prev, date: newDate }));
  };

  const handleSupplierInvoiceNoChange = (value) => {
    setInvoice(prev => ({ ...prev, supplierInvoiceNo: value }));
  };

  const handleProductChange = (event, product, shouldAutoAdd = false) => {
    console.log('handleProductChange called:', { product: product?.name, shouldAutoAdd });

    if (!product) return;

    // Store product with full info including alternate unit and rates
    const productWithRates = {
      ...product,
      basicUnit: product.basicUnit || product.unit || 'EA',
      alternateUnit: product.alternateUnit || '',
      purchaseRate: product.purchaseRate || product.price || 0,
      altPurchaseRate: product.altPurchaseRate || 0
    };

    // Update product and related fields with calculations
    handleNewItemChange('product', productWithRates);
    handleNewItemChange('description', product.description || '');
    handleNewItemChange('price', product.purchaseRate || product.price || 0);
    handleNewItemChange('vatRate', (product.vat !== undefined && product.vat !== null) ? product.vat : 15);
    handleNewItemChange('unit', product.basicUnit || product.unit || 'EA');

    // If auto-add is requested (Enter key was pressed), set flag for useEffect
    if (shouldAutoAdd) {
      console.log('Auto-add requested, setting flag for useEffect');
      setShouldAutoAddItem(true);
    }
  };

  // Keyboard navigation handlers

  const handleKeyDown = (event, itemId, field, fieldIndex) => {
    // Removed Enter key handling - no auto-add behavior
    // Only Tab key navigation is handled by the table component
  };

  const handleNewItemKeyDown = (event, field) => {
    if (event.key === 'Enter') {
      event.preventDefault();

      // If Enter is pressed on the product field and a product is selected, add the item immediately
      if (field === 'product' && newItem.product) {
        handleAddItemWithFocus();
        return;
      }
    }
  };

  const handleFocus = (element) => {
    if (element?.target) {
      element.target.select();
    } else if (element?.select) {
      element.select();
    }
  };

  // Wrap handleAddItem to focus on Qty field of newly added row
  const handleAddItemWithFocus = () => {
    handleAddItem();

    // Focus on Qty field of the newly added row (the row that was just added, not the new empty row)
    setTimeout(() => {
      console.log('Attempting to focus on qty field of newly added row');
      // Find all quantity fields in existing invoice items (not the new item row)
      const allQtyFields = document.querySelectorAll('[data-item-id][data-field="quantity"]');
      console.log('Total quantity fields found:', allQtyFields.length);

      // Get the last quantity field which belongs to the newly added row
      if (allQtyFields.length > 0) {
        const lastQtyField = allQtyFields[allQtyFields.length - 1];
        console.log('Focusing on qty field of the last added item (not new empty row)');
        lastQtyField.focus();
        // No .select() to prevent text selection
      } else {
        console.log('No quantity fields found to focus');
      }
    }, 150);
  };

  const handleSettingsClick = () => {
    // Settings click handler - handled by InvoiceItemsTable component
  };

  const handleColumnToggle = (column) => {
    setVisibleColumns(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const handlePaymentStatusChange = (event) => {
    setInvoice(prev => ({ ...prev, paymentStatus: event.target.value }));
  };

  const handlePaymentMethodChange = (value) => {
    setInvoice(prev => ({ ...prev, paymentMethod: value }));
  };

  const handlePaymentAmountChange = (value) => {
    setInvoice(prev => ({ ...prev, paymentAmount: value }));
  };

  const handlePaymentReferenceChange = (value) => {
    setInvoice(prev => ({ ...prev, paymentReference: value }));
  };

  // Handle submit with navigation
  const handleSubmit = async (status = 'draft') => {
    const response = await submitInvoice(status);
    if (response) {
      navigate('/purchases');
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.type}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      <Paper
        elevation={0}
        sx={{
          p: 0.5,
          borderRadius: '4px',
          maxWidth: '1800px',
          margin: '0 auto',
          boxShadow: 'none',
          border: 'none'
        }}
      >
        {/* Header Section */}
        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={() => navigate('/purchases')}
                sx={{ bgcolor: 'rgba(67, 97, 238, 0.1)' }}
              >
                <CloseIcon />
              </IconButton>
              <Box>
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                  New Purchase Invoice
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Create a new purchase invoice
                </Typography>
              </Box>
            </Box>
            <Alert severity="info" sx={{ fontSize: '0.875rem', ml: 0 }}>
              This invoice will don't be submitted to ZATCA upon completion. All fields marked with * are required.
            </Alert>
          </Box>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate('/purchases')}
              sx={{ fontSize: '0.875rem' }}
            >
              Cancel
            </Button>
          </Box>
        </Box>

        {/* Main Form Sections */}
        <Grid container spacing={0.5}>
          {/* Invoice Details Section */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 0.5,
                mb: 0.5,
                borderRadius: '4px',
                boxShadow: 'none',
                border: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
              }}
            >
              <PurchaseInvoiceDetails
                invoice={invoice}
                handleDateChange={handleDateChange}
                handleSupplierInvoiceNoChange={handleSupplierInvoiceNoChange}
                handlePaymentStatusChange={handlePaymentStatusChange}
              />
            </Paper>
          </Grid>

          {/* Supplier Section */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={0}
              sx={{
                p: 0.5,
                mb: 0.5,
                borderRadius: '4px',
                boxShadow: 'none',
                border: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.02)',
                },
              }}
            >
              <SupplierInformation
                invoice={invoice}
                suppliers={suppliers}
                handleSupplierChange={handleSupplierChange}
              />
            </Paper>
          </Grid>
        </Grid>

        {/* Items Table */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
          Invoice Items
        </Typography>

        <InvoiceItemsTable
          type="purchase"
          items={invoice.items}
          newItem={newItem}
          visibleColumns={visibleColumns}
          products={products}
          loading={loading || productsLoading} // Include products loading state
          onItemChange={handleItemChange}
          onRemoveItem={handleRemoveItem}
          onProductChange={handleProductChange}
          onNewItemChange={handleNewItemChange}
          onAddItem={handleAddItemWithFocus}
          onKeyDown={handleKeyDown}
          onNewItemKeyDown={handleNewItemKeyDown}
          onFocus={handleFocus}
          onSettingsClick={handleSettingsClick}
          onColumnToggle={handleColumnToggle}
          productInputRef={productInputRef}
        />

        {/* Payment Information and Invoice Summary Section */}
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box>
                <PaymentInformation
                  payments={invoice.payments}
                  paymentMethod={invoice.paymentMethod}
                  paymentAmount={invoice.paymentAmount}
                  paymentReference={invoice.paymentReference}
                  grandTotal={calculateTotal(invoice.items)}
                  totalPaid={getTotalPaid()}
                  remainingBalance={getRemainingBalance()}
                  onPaymentMethodChange={handlePaymentMethodChange}
                  onPaymentAmountChange={handlePaymentAmountChange}
                  onPaymentReferenceChange={handlePaymentReferenceChange}
                  onAddPayment={handleAddPayment}
                  onRemovePayment={handleRemovePayment}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Invoice Summary
                </Typography>
                <InvoiceSummary
                  invoice={{ ...invoice, setInvoice }}
                  calculateTotal={calculateTotal}
                  calculateSubtotal={calculateSubtotal}
                  calculateVat={calculateVat}
                  getTotalPaid={getTotalPaid}
                  getRemainingBalance={getRemainingBalance}
                  submitInvoice={handleSubmit}
                  loading={loading}
                  isEditMode={isEditMode}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Container>
  );
}

export default PurchaseInvoice;